/**
 * NaCl box (X25519 + XSalsa20-Poly1305) wrappers.
 * Pure functions — no module-level state, no logging.
 *
 * @module features/p2p-chat/lib/crypto
 */

import nacl from 'tweetnacl';
import {
  decodeBase64,
  decodeUTF8,
  encodeBase64,
  encodeUTF8,
} from 'tweetnacl-util';

const NONCE_LENGTH = nacl.box.nonceLength; // 24

/**
 * Generate a fresh ephemeral X25519 keypair.
 * Caller is responsible for keeping `secretKey` in a closure and never persisting it.
 */
export function generateKeyPair(): nacl.BoxKeyPair {
  return nacl.box.keyPair();
}

/**
 * Authenticated-encrypt `plaintext` for `theirPublicKey` using `mySecretKey`.
 * Returns base64 of `nonce || ciphertext` (24-byte nonce prepended).
 */
export function encrypt(
  plaintext: string,
  theirPublicKey: Uint8Array,
  mySecretKey: Uint8Array
): string {
  const nonce = nacl.randomBytes(NONCE_LENGTH);
  const messageBytes = decodeUTF8(plaintext);
  const cipher = nacl.box(messageBytes, nonce, theirPublicKey, mySecretKey);

  const combined = new Uint8Array(NONCE_LENGTH + cipher.length);
  combined.set(nonce, 0);
  combined.set(cipher, NONCE_LENGTH);
  return encodeBase64(combined);
}

/**
 * Authenticated-decrypt a base64 payload. Returns `null` if MAC verification fails
 * (i.e. tampering, wrong key, or malformed input). Never throws.
 */
export function decrypt(
  payload: string,
  theirPublicKey: Uint8Array,
  mySecretKey: Uint8Array
): string | null {
  let combined: Uint8Array;
  try {
    combined = decodeBase64(payload);
  } catch {
    return null;
  }
  if (combined.length <= NONCE_LENGTH) return null;

  const nonce = combined.slice(0, NONCE_LENGTH);
  const cipher = combined.slice(NONCE_LENGTH);

  const plain = nacl.box.open(cipher, nonce, theirPublicKey, mySecretKey);
  if (!plain) return null;

  try {
    return encodeUTF8(plain);
  } catch {
    return null;
  }
}

/** Encode a public key as base64 for transport over the WebRTC DataChannel. */
export function publicKeyToString(publicKey: Uint8Array): string {
  return encodeBase64(publicKey);
}

/** Decode a base64 public key. Throws on malformed input — caller must catch. */
export function publicKeyFromString(s: string): Uint8Array {
  return decodeBase64(s);
}
